/**
 * Plumbing Fixture Tool
 * 
 * Places plumbing fixtures (WC, Sink, etc.) with fixture unit calculations.
 */

import { v4 as uuidv4 } from 'uuid';
import { PlumbingFixtureElement, PlumbingFixtureGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class PlumbingFixtureTool extends BaseTool {
    id = 'plumbing_fixture';
    name = 'Plumbing Fixture';
    cursor = 'crosshair';

    private currentPoint: THREE.Vector3 | null = null;
    private rotation: number = 0;

    // Default Water Closet dimensions (Wall Mounted)
    private width = 400;
    private depth = 600;
    private height = 400;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        if (this.currentPoint) {
            this.createFixture();
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        // Find host or workplane
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === ' ') {
            this.rotation += Math.PI / 2;
            if (this.rotation >= Math.PI * 2) this.rotation = 0;
            this.updatePreview();
            event.preventDefault();
        } else if (event.key === 'Escape') {
            this.deactivate();
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (!this.currentPoint) {
            this.context.appStore.setToolPreview(null);
            return;
        }

        this.context.appStore.setToolPreview({
            type: 'box',
            position: this.currentPoint,
            size: [this.width / 1000, this.depth / 1000, this.height / 1000],
            rotation: [0, 0, this.rotation],
            color: '#ffffff' // White ceramic
        });
    }

    private createFixture() {
        if (!this.context || !this.currentPoint) return;

        const geometryData: PlumbingFixtureGeometryData = {
            position: [this.currentPoint.x, this.currentPoint.y, this.currentPoint.z + (this.height / 2000)], // Center Z
            rotation: this.rotation,
            width: this.width,
            depth: this.depth,
            height: this.height
        };

        const fixture: PlumbingFixtureElement = {
            id: uuidv4(),
            category: 'Plumbing Fixtures',
            type: 'Water Closet - Wall Mounted',
            parameters: {
                'Mark': 'WC-1',
                'WFU': '4', // Waste Fixture Units
                'CWFU': '2.5', // Cold Water Fixture Units
                'HWFU': '0',
                'Wet Venting': 'false',
                'System Type': 'Sanitary'
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(fixture);
    }
}
